/**
 * LiveBanner — Plan 04-07 / LIVE-01..03
 *
 * Conditional top-of-page banner that promotes live commerce events.
 * Operator-toggled via site.json.liveShoppingNow=true.
 *
 * Hard freeze D-22: brand-red gradient #d32f2f → #7b1f1f preserved verbatim.
 * LGPD: data-clarity-mask="true" on host handle span (A4 risk — handle correlatable with operator identity).
 * V7: trackLiveBannerClick logs hashed values — never raw URL or handle.
 *
 * Z-index: z-[57] — ABOVE ConsentBanner z-[55] BUT BELOW ConsentDialog z-[60]
 * per UI-SPEC §Z-Index Stack rule 6.
 */
import { useState, useEffect } from "react";
import { Radio, X, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { trackLiveBannerClick } from "@/lib/analytics";
import siteConfig from "@/config/site.json";

/** Hash a string for V7 ASVS — defensive logging (V7 — never log raw URL/handle). */
function hashStr(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}

export function LiveBanner() {
  const config = siteConfig as {
    liveShoppingNow?: boolean;
    liveShoppingPlatformUrl?: string;
    liveShoppingHostHandle?: string;
  };
  const isOn = config.liveShoppingNow === true;
  const url = config.liveShoppingPlatformUrl ?? "";
  const handle = config.liveShoppingHostHandle ?? "";

  const dismissKey = `liveBannerDismissed:${url}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try { return window.sessionStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isOn) return;
    if (!url) {
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn("[LiveBanner] liveShoppingNow=true but liveShoppingPlatformUrl is empty — banner suppressed.");
      }
      return;
    }
    // Animate in slide-down after brief delay
    const t = window.setTimeout(() => setVisible(true), 50);
    return () => window.clearTimeout(t);
  }, [isOn, url]);

  if (!isOn || !url || dismissed) return null;

  const handleCtaClick = () => {
    trackLiveBannerClick({ platform_url_hash: hashStr(url), host_handle_hash: hashStr(handle) });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleClose = () => {
    try { window.sessionStorage.setItem(dismissKey, "1"); } catch { /* fail-open */ }
    setDismissed(true);
  };

  return (
    <aside
      role="region"
      aria-label="Banner ao vivo"
      className={cn(
        "fixed inset-x-0 top-0 z-[57]",
        "bg-gradient-to-r from-[#d32f2f] to-[#7b1f1f]",
        "shadow-[0_4px_16px_rgba(0,0,0,0.15)]",
        "px-4 py-2 sm:px-6 sm:py-3",
        "transition-transform duration-300 ease-out motion-reduce:transition-none",
        visible ? "translate-y-0" : "-translate-y-full"
      )}
    >
      <div className="mx-auto flex max-w-[960px] items-center justify-between gap-3 sm:gap-6">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Badge
            className="bg-white/15 text-white border border-white/20 motion-safe:animate-[pulse-live_1.4s_ease-in-out_infinite]"
            aria-label="Indicador ao vivo"
          >
            <span
              aria-hidden="true"
              className="mr-1 inline-block motion-safe:animate-[blink-live_1s_steps(2,end)_infinite]"
            >
              🔴
            </span>
            <Radio size={12} className="mr-1" aria-hidden="true" /> AO VIVO agora
          </Badge>
          <p className="text-base sm:text-lg font-bold text-white truncate">
            A gente está ao vivo no <span data-clarity-mask="true">{handle}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleCtaClick}
            aria-label={`Abrir live em ${url}`}
            className="inline-flex items-center gap-1 rounded-xl bg-white/95 px-3 py-2 text-sm font-bold text-[#7b1f1f] hover:bg-white min-h-[44px] focus-visible:ring-2 focus-visible:ring-white"
          >
            Entrar na live <ExternalLink size={14} />
          </button>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar banner ao vivo"
            className="text-white/80 hover:text-white p-1.5 min-h-[44px] focus-visible:ring-2 focus-visible:ring-white"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-white/85 text-center hidden sm:block">
        Promo só durante a live · Avise no grupo!
      </p>
    </aside>
  );
}
